<script setup lang="ts">
import { computed, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { gCollectorStore } from '@/stores/gCollectorStore';
import Tooltip from '@/components/Common/Tooltip.vue';
import { isValidFee, exampleFee, formatFee, stepLabel, type FeeBounds, type FeeUnit } from '@/utils/feeValidation';

const { t } = useI18n();
const gStore = gCollectorStore();
const { tenant } = storeToRefs(gStore);

import { storeToRefs } from 'pinia'

const adminFee = defineModel<string | number>('adminFee', { default: '' });
const brokerAdminFee = defineModel<string | number>('brokerAdminFee', { default: '' });
// Reported upward so the quote page can block "Next"/"Proceed" while invalid.
const valid = defineModel<boolean>('valid', { default: true });

const props = defineProps<{
    selectedScheme: any;
    adminFeeEditable?: boolean;
    allowAdminFeeEdit?: boolean;
    isOnAccount?: boolean;
    bounds?: FeeBounds | null;
    locked?: boolean;
}>()

const isOffAccount = computed(() => !props.isOnAccount);
const brokerFeeUnit = computed<FeeUnit>(() => (isOffAccount.value ? '%' : '£'));

const showAdminFeeInput = computed(() => props.adminFeeEditable && props.allowAdminFeeEdit !== false);

const feeError = computed<string | null>(() => {
    // Locked = fixed backend rate shown as text; nothing to validate.
    if (props.locked || props.adminFeeEditable || !props.bounds) return null;

    const raw = String(brokerAdminFee.value ?? '').trim();
    const value = Number(raw);
    if (raw === '' || !Number.isFinite(value)) return t('quotes.quote.summary.scheme.feeRequired');
    if (isValidFee(value, props.bounds)) return null;

    const unit = brokerFeeUnit.value;
    return t('quotes.quote.summary.scheme.feeInvalidRange', {
        min: formatFee(props.bounds.min, unit),
        max: formatFee(props.bounds.max, unit),
        step: stepLabel(props.bounds.increment),
        example: formatFee(exampleFee(props.bounds, value), unit),
    });
});

watchEffect(() => { valid.value = feeError.value === null; });
const brokerFeeLabel = computed(() =>
    isOffAccount.value
        ? t('quotes.quote.summary.scheme.commission')
        : 'Broker admin fee');
const brokerFeeTooltip = computed(() =>
    isOffAccount.value
        ? 'Please use the input box to adjust your commission. This will be adjusted within the final premium.'
        : 'Please use the input box to adjust your administration fee. This will be included within the final premium.'
);

const emit = defineEmits<{
    (e: 'apply'): void;
}>()

function onFocusOut(event: FocusEvent)
{
    const container = event.currentTarget as HTMLElement;
    const next = event.relatedTarget as Node | null;
    if (next && container.contains(next)) return;
    emit('apply');
}
</script>

<template>
    <div class="card mB-10 h-100 w-100">
        <div class="card-header bg-transparent">
            <h5 class="card-title mB-0">Admin Fees</h5>
        </div>
        <div class="card-body" v-if="selectedScheme" @focusout="onFocusOut">
            <div class="row align-items-end mb-3">
                <div class="col-6">
                    <div class="mb-1">
                        <span class="font-weight-medium">Admin fee</span>
                        <Tooltip class="d-inline-block ml-1">
                            <i class="far fa-circle-question text-muted"></i>
                            <template #content>
                                Please use the input box to adjust your administration fee. This will be included within the final premium.
                            </template>
                        </Tooltip>
                    </div>
                    <div class="small text-muted">(Admin CETA fee)</div>
                </div>
                <div class="col-6">
                    <div class="input-group input-group-sm" v-if="showAdminFeeInput">
                        <div class="input-group-prepend">
                            <span class="input-group-text">£</span>
                        </div>
                        <input v-model="adminFee" type="text" class="form-control" />
                    </div>
                    <div v-else>
                        {{ adminFee ? `£${adminFee}` : '-' }}
                    </div>
                </div>
            </div>

            <div class="row align-items-end" v-if="!adminFeeEditable">
                <div class="col-6">
                    <div class="mb-1">
                        <span class="font-weight-medium">{{ brokerFeeLabel }}</span>
                        <Tooltip class="d-inline-block ml-1">
                            <i class="far fa-circle-question text-muted"></i>
                            <template #content>
                                {{ brokerFeeTooltip }}
                            </template>
                        </Tooltip>
                    </div>
                    <div class="small text-muted">{{ t('quotes.quote.summary.scheme.brokerAdminFeeDescription') }}</div>
                </div>
                <div class="col-6">
                    <div v-if="locked">
                        {{ (brokerAdminFee ?? '') !== ''
                            ? (isOffAccount ? `${brokerAdminFee}%` : `£${brokerAdminFee}`)
                            : '-' }}
                    </div>
                    <template v-else>
                        <div class="input-group input-group-sm">
                            <div class="input-group-prepend">
                                <span class="input-group-text">{{ isOffAccount ? '%' : '£' }}</span>
                            </div>
                            <input
                                v-model="brokerAdminFee"
                                type="text"
                                inputmode="decimal"
                                class="form-control"
                                :class="{ 'is-invalid': feeError }"
                            />
                        </div>
                        <div v-if="feeError" class="text-danger small mt-1">{{ feeError }}</div>
                    </template>
                </div>
            </div>
        </div>
        <div class="card-body" v-else>
            <p class="text-muted mB-0">Please select a scheme to modify the admin fee</p>
        </div>
    </div>
</template>
